import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

/**
 * Artist Dashboard — daily analytics roll-ups.
 *
 * Reads from the `artist_daily_stats` Mongo collection via
 * GET /api/artists/daily-stats/:id (artist owner or admin only). Data is
 * populated nightly by the analytics-rollup Lambda; values reflect the
 * trailing 30 ET days as of yesterday's roll-up.
 */
@inject('statsStore')
@observer
class Dashboard extends Component {
	// Mobx observables: prevProps.artist === this.props.artist always (same
	// observable instance), so we can't detect "id arrived" by comparing
	// prevProps. Track what we've fetched for instead.
	_fetchedForId = null;

	componentDidMount () {
		this.maybeFetch();
	}

	componentDidUpdate () {
		this.maybeFetch();
	}

	maybeFetch () {
		const { artist, statsStore } = this.props;
		const id = artist?.id;
		if (id && id !== this._fetchedForId) {
			this._fetchedForId = id;
			statsStore.fetchDailyStats(id, 30);
		}
	}

	render () {
		const { statsStore, artist } = this.props;

		if (!artist?.id || statsStore.isLoadingDaily) {
			return this.renderLoading();
		}

		const stats = statsStore.dailyStats;
		if (!stats) {
			return this.renderEmpty();
		}

		const { totals = {}, days = [], fromDay, toDay, subscribers = {} } = stats;
		const hasNoActivity = days.length === 0 && !subscribers.active;
		if (hasNoActivity) {
			return this.renderNoActivity({ fromDay, toDay });
		}

		return (
			<div className="cell small-12">
				<div className="grid-x grid-padding-x" style={{ marginTop: '4vh' }}>
					<div className="cell small-12">
						<h2 style={{ margin: 0 }}>Last 30 days</h2>
						<p style={{ opacity: 0.6, marginTop: '0.25rem' }}>
							{ fromDay } → { toDay } &nbsp;·&nbsp; updated nightly
						</p>
					</div>

					{ this.renderTotalCard('Active Subscribers', subscribers.active) }
					{ this.renderTotalCard('New Subs (30d)',     totals.newSubs) }
					{ this.renderTotalCard('Cancels (30d)',      totals.cancellations) }
					{ this.renderTotalCard('Listens',            totals.listens) }
					{ this.renderTotalCard('Listeners',          totals.listeners) }
					{ this.renderTotalCard('Favorites',          totals.favorites) }
				</div>

				{ days.length > 0 && (
					<div className="grid-x grid-padding-x" style={{ marginTop: '3vh' }}>
						<div className="cell small-12">
							<h3>Daily breakdown</h3>
							{ this.renderDailyTable(days) }
						</div>
					</div>
				) }
			</div>
		);
	}

	renderTotalCard (label, value) {
		return (
			<div className="cell small-12 medium-4 large-2" key={ label }>
				<div style={{
					padding: '1.5rem',
					background: 'rgba(255,255,255,0.04)',
					borderRadius: '0.5rem',
					marginTop: '1rem',
				}}>
					<div style={{ fontSize: '0.85rem', textTransform: 'uppercase', opacity: 0.6 }}>
						{ label }
					</div>
					<div style={{ fontSize: '2.5rem', fontWeight: 600, marginTop: '0.5rem' }}>
						{ Number(value || 0).toLocaleString() }
					</div>
				</div>
			</div>
		);
	}

	renderDailyTable (days) {
		// Newest first for at-a-glance.
		const ordered = [...days].reverse();
		const numCell = { textAlign: 'right' };
		return (
			<table style={{ width: '100%', marginTop: '1rem' }}>
				<thead>
					<tr>
						<th style={{ textAlign: 'left' }}>Day</th>
						<th style={ numCell }>Listens</th>
						<th style={ numCell }>Listeners</th>
						<th style={ numCell }>Favorites</th>
						<th style={ numCell }>New Subs</th>
						<th style={ numCell }>Cancels</th>
					</tr>
				</thead>
				<tbody>
					{ ordered.map(d => (
						<tr key={ d.day }>
							<td>{ d.day }</td>
							<td style={ numCell }>{ d.listens.toLocaleString() }</td>
							<td style={ numCell }>{ d.listeners.toLocaleString() }</td>
							<td style={ numCell }>{ d.favorites.toLocaleString() }</td>
							<td style={ numCell }>{ d.newSubs.toLocaleString() }</td>
							<td style={ numCell }>{ d.cancellations.toLocaleString() }</td>
						</tr>
					)) }
				</tbody>
			</table>
		);
	}

	renderLoading () {
		return (
			<div className="cell small-12 text-center" style={{ marginTop: '20vh' }}>
				<div className="no-result-text dropfade-text">Loading…</div>
			</div>
		);
	}

	renderEmpty () {
		return (
			<div className="cell small-12 text-center" style={{ marginTop: '20vh' }}>
				<div className="no-result-text dropfade-text">No stats available yet</div>
				<div className="no-result-text dropfade-subtext margin-top-1" style={{ fontSize: '1.25em' }}>
					Daily numbers appear here after your first day with listens. Roll-ups happen overnight.
				</div>
			</div>
		);
	}

	renderNoActivity ({ fromDay, toDay }) {
		return (
			<div className="cell small-12 text-center" style={{ marginTop: '20vh' }}>
				<div className="no-result-text dropfade-text">No activity in the last 30 days</div>
				<div className="no-result-text dropfade-subtext margin-top-1" style={{ fontSize: '1.25em' }}>
					{ fromDay } → { toDay }
				</div>
			</div>
		);
	}
}

export default Dashboard;
